import { Injectable } from '@angular/core'
import { MatDialogConfig } from '@angular/material/dialog'
import { SharedModule } from '@tv3/shared/shared.module'
import { ApiConnectionDialogComponent } from '@tv3/containers/channels/connection-detail/api/api-connection-dialog.component'
import { ComponentType } from '@angular/cdk/portal'
import { IcalConnectionDialogComponent } from '@tv3/containers/channels/connection-detail/ical/ical-connection-dialog.component'
import { ChannelNameTokens, DialogService, loadRental } from '@tokeet-frontend/tv3-platform'
import { ConnectionStatusTexts, ConnectionView } from '@tv3/store/connection/connection.view'
import { AuthService } from '@tv3/services/auth.service'
import { PropertyContentHelperService } from '../channel-connect/property-content-helper.service'

import { GVRConnectionOverlayService } from './feed/gvr-connection-overlay/gvr-connection-overlay.service'
import { HoliduConnectionOverlayService } from './feed/holidu-connection-overlay/holidu-connection-overlay.service'
import { HomeAwayConnectionOverlayService } from './feed/homeaway-connection-overlay/homeaway-connection-overlay.service'
import { HvmiConnectionOverlayService } from './feed/hvmi-connection-overlay/hvmi-connection-overlay.service'
import { RentalsUnitedConnectionOverlayService } from './feed/ru-connection-overlay/ru-connection-overlay.service'
import { ChannelListingWizardsService } from '../channel-connect/listing-connect-wizards/index.service'
import { Store } from '@ngrx/store'
import { HopperConnectionOverlayService } from './feed/hopper-connection-overlay/hopper-connection-overlay.service'
import { Observable } from 'rxjs'

@Injectable({
  providedIn: SharedModule,
})
export class ConnectionDialogService {
  constructor(
    private dialog: DialogService,
    private auth: AuthService,
    private store: Store<any>,
    private channelListingWizards: ChannelListingWizardsService,
    private propertyContentHelperService: PropertyContentHelperService,
    private homeAwayConnectionOverlayService: HomeAwayConnectionOverlayService,
    private hvmiConnectionOverlayService: HvmiConnectionOverlayService,
    private holiduConnectionOverlayService: HoliduConnectionOverlayService,
    private gvrConnectionOverlay: GVRConnectionOverlayService,
    private hopperConnectionOverlay: HopperConnectionOverlayService,
    private rentalsUnitedConnectionOverlay: RentalsUnitedConnectionOverlayService
  ) {}

  private open<T>(connectionId: string, component: ComponentType<T>) {
    const defaultConfig: MatDialogConfig = {
      data: { connectionId },
      panelClass: ['drawer-full-90'],
    }

    const dialogRef = this.dialog.openDrawer(component, defaultConfig)

    return dialogRef.afterClosed()
  }

  openSide(connection: ConnectionView, wizard = true): Observable<any> {
    const channel = connection.channel
    if (connection.rentalId) {
      this.store.dispatch(loadRental({ id: connection.rentalId }))
    }

    if (connection.isICal) {
      return this.open(connection.id, IcalConnectionDialogComponent)
    }

    if (connection.isCreatedOnTokeet && connection.isIncompleted) {
      return this.propertyContentHelperService.openContentOverlay(channel, connection)
    }

    switch (channel.name) {
      case ChannelNameTokens.Hvmi:
        return this.hvmiConnectionOverlayService.openSide(connection).afterClosed()
      case ChannelNameTokens.Holidu:
        return this.holiduConnectionOverlayService.openSide(connection).afterClosed()
      case ChannelNameTokens.GoogleVacationRentals:
        return this.gvrConnectionOverlay.openSide(connection).afterClosed()
      case ChannelNameTokens.RentalsUnited:
        return this.rentalsUnitedConnectionOverlay.openSide(connection).afterClosed()
      case ChannelNameTokens.Homeaway:
        return this.homeAwayConnectionOverlayService.openSide(connection)
      case ChannelNameTokens.Hopper:
        return this.hopperConnectionOverlay.openSide(connection).afterClosed()
      default:
        break
    }

    if (this.auth.isReadOnly()) {
      return this.open(connection.id, ApiConnectionDialogComponent)
    }
    // TV4-6062: the mapped room no longer exists on the channel — the listing wizard would only
    // link/push against the dead room id. Open the detail drawer so the user can inspect and unlink.
    if (
      connection.statusText === ConnectionStatusTexts.Active ||
      connection.isAirbnbSyncPaused ||
      connection.roomMissing ||
      !wizard
    ) {
      return this.open(connection.id, ApiConnectionDialogComponent)
    }

    // A Pending (linked but not fully pushed) connection opens its listing wizard, which
    // resumes from the first incomplete step — it reads the durable link/push timestamps
    // on the connection, so this is a progress view, not a restart.
    return this.channelListingWizards.openWizard(connection.channel, connection)
  }
}
